import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  RefreshCw,
  Wallet,
  X,
} from 'lucide-react';
import { apiService } from '../../services/api.service';
import { useCashSession, requestCashSessionClose } from '../../components/CashSessionBar';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  formatTime,
  humanizeTreasuryError,
  CTA_PRIMARY,
} from '../treasury/treasuryUi';
import { TreasurySkeleton } from '../treasury/TreasuryFeedback';

type ModalKind = 'in' | 'out' | 'open' | null;

type MovementBadge =
  | 'COBRO'
  | 'DESEMBOLSO'
  | 'INGRESO MANUAL'
  | 'EGRESO MANUAL'
  | 'TRANSFERENCIA RECIBIDA'
  | 'TRANSFERENCIA ENVIADA'
  | 'AJUSTE'
  | 'INGRESO'
  | 'EGRESO'
  | 'OTRO';

function isInflow(type: string) {
  return ['IN', 'INCOME', 'TRANSFER_IN', 'ADJUSTMENT_IN', 'OPENING', 'REVERSAL'].includes(type);
}

function classifyMovement(m: any): MovementBadge {
  const src = String(m.sourceType ?? '').toUpperCase();
  const type = String(m.type ?? '');
  if (m.paymentId || src === 'PAYMENT' || src.includes('PAYMENT')) return 'COBRO';
  if (src.includes('DISBURSE') || m.loanDisbursement || (m.loanId && !m.paymentId && !isInflow(type))) {
    return 'DESEMBOLSO';
  }
  if (type === 'TRANSFER_IN' || (src === 'CASH_TRANSFER' && isInflow(type))) {
    return 'TRANSFERENCIA RECIBIDA';
  }
  if (type === 'TRANSFER_OUT' || (src === 'CASH_TRANSFER' && !isInflow(type))) {
    return 'TRANSFERENCIA ENVIADA';
  }  if (m.adjustmentId || src.includes('ADJUST')) return 'AJUSTE';
  if (src === 'MANUAL' || (!m.paymentId && !m.loanId && !m.transferId && !m.adjustmentId)) {
    if (isInflow(type)) return 'INGRESO MANUAL';
    return 'EGRESO MANUAL';
  }
  if (isInflow(type)) return 'INGRESO';
  if (type === 'OUT' || type === 'EXPENSE') return 'EGRESO';
  return 'OTRO';
}

const BADGE_CLASS: Record<MovementBadge, string> = {
  COBRO: 'bg-emerald-100 text-emerald-900',
  DESEMBOLSO: 'bg-violet-100 text-violet-900',
  'INGRESO MANUAL': 'bg-sky-100 text-sky-900',
  'EGRESO MANUAL': 'bg-orange-100 text-orange-900',
  'TRANSFERENCIA RECIBIDA': 'bg-blue-100 text-blue-900',
  'TRANSFERENCIA ENVIADA': 'bg-indigo-100 text-indigo-900',
  AJUSTE: 'bg-amber-100 text-amber-900',
  INGRESO: 'bg-emerald-50 text-emerald-800',
  EGRESO: 'bg-rose-50 text-rose-800',
  OTRO: 'bg-slate-100 text-slate-700',
};

function operationLink(m: any): { to: string; label: string } | null {
  if (m.paymentId) {
    if (m.payment?.loanId || m.loanId) {
      return { to: `/loans/${m.payment?.loanId || m.loanId}`, label: 'Ver operación' };
    }
    return { to: '/payments', label: 'Ver operación' };
  }
  if (m.loanId) return { to: `/loans/${m.loanId}`, label: 'Ver operación' };
  if (m.transferId) return { to: '/treasury/transfers', label: 'Ver operación' };
  if (m.adjustmentId) return { to: '/treasury/config', label: 'Ver operación' };
  if (String(m.sourceType ?? '').toUpperCase().includes('EXPENSE')) {
    return { to: '/payables', label: 'Ver operación' };
  }
  return null;
}

export function DailyCashPage() {
  const navigate = useNavigate();
  const { session, loading: sessionLoading, refresh } = useCashSession();
  const { can } = usePermissions();
  // Feature catalog uses cash-mine; legacy coarse `cash` may still appear for older grants.
  const canCash =
    can('cash-mine', 'read') ||
    can('cash-mine', 'write') ||
    can('cash', 'read') ||
    can('cash', 'write');
  const [searchParams, setSearchParams] = useSearchParams();

  const [registers, setRegisters] = useState<any[]>([]);
  const [concepts, setConcepts] = useState<any[]>([]);
  const [balance, setBalance] = useState<any | null>(null);
  const [movements, setMovements] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [modal, setModal] = useState<ModalKind>(null);
  const [showMovements, setShowMovements] = useState(true);

  const [openForm, setOpenForm] = useState({ cashRegisterId: '', cashAdded: '0' });
  const [priorBalance, setPriorBalance] = useState<number | null>(null);
  const [manualForm, setManualForm] = useState({
    conceptId: '',
    amount: '',
    description: '',
    reference: '',
  });

  const fondoConcept = useMemo(() => concepts.find((c) => c.code === 'FONDO'), [concepts]);
  const egresoConcept = useMemo(() => concepts.find((c) => c.code === 'EGRESO'), [concepts]);

  const reloadSessionData = useCallback(async () => {
    if (!session?.id) {
      setBalance(null);
      setMovements([]);
      return;
    }
    try {
      const [bal, movs] = await Promise.all([
        apiService.getCashSessionBalance(session.id),
        apiService.listCashMovements({ sessionId: session.id }),
      ]);
      setBalance(bal);
      setMovements(Array.isArray(movs) ? movs : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'Error al cargar la caja');
    }
  }, [session?.id]);

  useEffect(() => {
    Promise.all([
      apiService.getCashRegisters(undefined, undefined, { forSession: true }),
      apiService.listCashConcepts(),
    ])
      .then(([regs, cons]) => {
        setRegisters(regs);
        setConcepts(cons);
        const preferred = regs.find((r: any) => r.isDefault) ?? regs[0];
        if (preferred?.id) {
          setOpenForm((f) => (f.cashRegisterId ? f : { ...f, cashRegisterId: preferred.id }));
        }
        setManualForm((f) => ({
          ...f,
          conceptId: f.conceptId || cons.find((c: any) => c.code === 'FONDO')?.id || '',
        }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    void reloadSessionData();
  }, [reloadSessionData]);

  useEffect(() => {
    if (searchParams.get('open') === '1' && !session) {
      setModal('open');
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, session]);

  useEffect(() => {
    if (!openForm.cashRegisterId || modal !== 'open') {
      setPriorBalance(null);
      return;
    }
    let cancelled = false;
    apiService
      .getCashRegisterLedgerBalance(openForm.cashRegisterId)
      .then((r) => {
        if (!cancelled) setPriorBalance(Number(r.balance ?? 0));
      })
      .catch(() => {
        if (!cancelled) setPriorBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, [openForm.cashRegisterId, modal]);

  const cashAddedNum = parseFloat(openForm.cashAdded) || 0;
  const availableAfterOpen =
    priorBalance != null ? Math.round((priorBalance + cashAddedNum) * 100) / 100 : null;

  const selectedRegister = registers.find((r) => r.id === openForm.cashRegisterId);

  const daySummary = useMemo(() => {
    let income = 0;
    let expense = 0;
    let collections = 0;
    let disbursements = 0;
    let transferIn = 0;
    let transferOut = 0;
    for (const m of movements) {
      const amt = Number(m.amount) || 0;
      const badge = classifyMovement(m);
      if (badge === 'COBRO') {
        collections += amt;
        continue;
      }
      if (badge === 'DESEMBOLSO') {
        disbursements += amt;
        continue;
      }
      if (badge === 'TRANSFERENCIA RECIBIDA') {
        transferIn += amt;
        continue;
      }
      if (badge === 'TRANSFERENCIA ENVIADA') {
        transferOut += amt;
        continue;
      }
      if (isInflow(m.type)) income += amt;
      else expense += amt;
    }
    return { income, expense, collections, disbursements, transferIn, transferOut };
  }, [movements]);

  const movementRows = useMemo(() => {
    const opening = Number(balance?.openingBalance ?? session?.openingBalance ?? 0);
    const sorted = [...movements].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    let running = opening;
    return sorted.map((m) => {
      const amt = Number(m.amount) || 0;
      const inflow = isInflow(m.type);
      running = Math.round((running + (inflow ? amt : -amt)) * 100) / 100;
      return {
        m,
        badge: classifyMovement(m),
        entrada: inflow ? amt : null,
        salida: inflow ? null : amt,
        saldo: running,
        link: operationLink(m),
      };
    });
  }, [movements, balance, session]);


  const openSession = async () => {
    if (!openForm.cashRegisterId) return;
    if (openForm.cashAdded === '' || Number.isNaN(cashAddedNum) || cashAddedNum < 0) {
      setError('Indicá el efectivo que agregás al abrir (puede ser 0).');
      return;
    }
    if (priorBalance == null) {
      setError('No se pudo obtener el saldo previo de la caja.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiService.openCashSession({
        cashRegisterId: openForm.cashRegisterId,
        openingBalance: availableAfterOpen!,
      });
      setModal(null);
      setOpenForm((f) => ({ ...f, cashAdded: '0' }));
      await refresh();
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(' ') : msg || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async (kind: 'in' | 'out') => {
    if (!session?.id) return;
    setBusy(true);
    setError('');
    try {
      await apiService.addCashMovement(session.id, {
        type: kind === 'in' ? 'IN' : 'OUT',
        amount: parseFloat(manualForm.amount),
        conceptId:
          manualForm.conceptId ||
          (kind === 'in' ? fondoConcept?.id : egresoConcept?.id) ||
          '',
        description: manualForm.description || undefined,
        reference: manualForm.reference || undefined,
      });
      setManualForm((f) => ({ ...f, amount: '', description: '', reference: '' }));
      setModal(null);
      await refresh();
      await reloadSessionData();
    } catch (e: any) {
      setError(humanizeTreasuryError(e?.response?.data?.message || e.message));
    } finally {
      setBusy(false);
    }
  };


  if (!canCash) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        No tenés permiso para operar la caja del día.
      </div>
    );
  }

  if (sessionLoading) {
    return <TreasurySkeleton kind="cash" />;
  }

  /* ---------- SIN SESIÓN ---------- */
  if (!session) {
    return (
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mi caja</h1>
          <p className="text-sm text-slate-600 mt-1">Caja del día · sesión del cajero</p>
        </div>

        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center space-y-4">
          <Wallet className="h-10 w-10 text-slate-400 mx-auto" />
          <div>
            <p className="text-lg font-semibold text-slate-900">No tenés una caja abierta.</p>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">
              Para cobrar o desembolsar dinero en efectivo necesitás una caja operativa con sesión
              abierta.
            </p>
          </div>
          {registers.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 inline-block">
              Sin caja asignada — pedile al administrador que te asigne una operativa.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => setModal('open')}
              className={CTA_PRIMARY + " gap-2 px-4 py-2.5 font-medium"}
            >
              Abrir caja
            </button>
          )}
          <p className="text-sm">
            <Link to="/treasury/position" className="text-blue-700 hover:underline inline-flex items-center gap-1">
              <Landmark className="h-3.5 w-3.5" /> ¿Necesitás fondos? Ver Tesorería
            </Link>
          </p>
        </div>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
        )}

        {modal === 'open' && (
          <OpenCashModal
            registers={registers}
            openForm={openForm}
            setOpenForm={setOpenForm}
            priorBalance={priorBalance}
            cashAddedNum={cashAddedNum}
            availableAfterOpen={availableAfterOpen}
            selectedRegister={selectedRegister}
            busy={busy}
            error={error}
            onClose={() => {
              setModal(null);
              setError('');
            }}
            onConfirm={() => void openSession()}
          />
        )}
      </div>
    );
  }

  /* ---------- CON SESIÓN ---------- */
  const branchName = session.cashRegister?.branch?.name ?? '—';
  const cajaNum = session.cashRegister?.number ?? '—';
  const operator = session.user
    ? `${session.user.firstName ?? ''} ${session.user.lastName ?? ''}`.trim()
    : '—';
  const openedAt = session.openedAt
    ? new Date(session.openedAt).toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';
  const disponible = Number(balance?.systemBalance ?? session.systemBalance ?? 0);

  return (
    <div className="space-y-6 max-w-5xl">
      {session.staleDay && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-950">Esta caja continúa abierta desde ayer.</p>
              <p className="text-sm text-amber-900/90 mt-0.5">
                {session.staleDayMessage ??
                  'Revisá movimientos y cerrá indicando el efectivo contado antes de seguir.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => requestCashSessionClose()}
            className="shrink-0 rounded-md bg-amber-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-900"
          >
            Revisar / cerrar
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mi caja</h1>
          <p className="mt-1 text-sm text-slate-700">
            Caja Operativa {cajaNum}
            <span className="text-slate-400"> · </span>
            Sucursal {branchName}
          </p>
          <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
            <div>
              <dt className="inline text-slate-500">Abierta · </dt>
              <dd className="inline font-medium text-slate-800">{openedAt}</dd>
            </div>
            <div>
              <dt className="inline text-slate-500">Operador · </dt>
              <dd className="inline font-medium text-slate-800">{operator || '—'}</dd>
            </div>
            <div>
              <dt className="inline text-slate-500">Estado · </dt>
              <dd className="inline">
                <span
                  className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                    session.staleDay ? 'bg-amber-200 text-amber-950' : 'bg-emerald-100 text-emerald-900'
                  }`}
                >
                  {session.staleDay ? 'ABIERTA · DÍA ANTERIOR' : 'ABIERTA'}
                </span>
              </dd>
            </div>
          </dl>
        </div>
        <button
          type="button"
          onClick={() => void reloadSessionData().then(() => refresh())}
          disabled={busy}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
      )}

      <section className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6">
        <p className="text-sm font-medium text-slate-300 uppercase tracking-wide">Disponible</p>
        <p className="mt-2 text-3xl sm:text-4xl font-semibold tabular-nums">{formatMoney(disponible)}</p>
        <p className="mt-2 text-xs text-slate-300">
          Saldo de la sesión (apertura + movimientos del día). Fuente: ledger de sesión.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Resumen del día</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            ['Ingresos', daySummary.income],
            ['Egresos', daySummary.expense],
            ['Cobros', daySummary.collections],
            ['Desembolsos', daySummary.disbursements],
          ].map(([label, value]) => (
            <div key={String(label)} className="rounded-lg border bg-white p-3">
              <p className="text-xs text-slate-500">{label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900">
                {formatMoney(Number(value))}
              </p>
            </div>
          ))}
        </div>
        {(daySummary.transferIn > 0 || daySummary.transferOut > 0) && (
          <p className="text-xs text-slate-500">
            Transferencias · recibidas {formatMoney(daySummary.transferIn)} · enviadas{' '}
            {formatMoney(daySummary.transferOut)}
          </p>
        )}
      </section>

      <section className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setManualForm((f) => ({
              ...f,
              conceptId: fondoConcept?.id || f.conceptId,
            }));
            setModal('in');
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800"
        >
          <ArrowDownLeft className="h-4 w-4" /> Registrar ingreso
        </button>
        <button
          type="button"
          onClick={() => {
            setManualForm((f) => ({
              ...f,
              conceptId: egresoConcept?.id || f.conceptId,
            }));
            setModal('out');
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-rose-700 text-white text-sm font-medium hover:bg-rose-800"
        >
          <ArrowUpRight className="h-4 w-4" /> Registrar egreso
        </button>
        <button
          type="button"
          onClick={() => navigate('/cash/move?type=cashier')}
          className={CTA_PRIMARY + " gap-2 px-4 py-2.5 font-medium"}
        >
          Mover fondos
        </button>
        <button
          type="button"
          onClick={() => setShowMovements((v) => !v)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm hover:bg-slate-50"
        >
          {showMovements ? 'Ocultar movimientos' : 'Ver movimientos'}
        </button>
        <button
          type="button"
          onClick={() => requestCashSessionClose()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-red-200 text-red-700 text-sm hover:bg-red-50"
        >
          Cerrar caja
        </button>
      </section>

      {showMovements && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-900">Movimientos de la sesión</h2>
          <div className="overflow-x-auto border rounded-lg bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Hora</th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Concepto</th>
                  <th className="px-3 py-2 font-medium">Referencia</th>
                  <th className="px-3 py-2 font-medium text-right">Entrada</th>
                  <th className="px-3 py-2 font-medium text-right">Salida</th>
                  <th className="px-3 py-2 font-medium text-right">Saldo</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {movementRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                      Sin movimientos en esta sesión todavía.
                    </td>
                  </tr>
                ) : (
                  movementRows.map(({ m, badge, entrada, salida, saldo, link }) => (
                    <tr key={m.id} className="border-t">
                      <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                        {m.createdAt ? formatTime(m.createdAt) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${BADGE_CLASS[badge]}`}
                        >
                          {badge}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {m.concept?.name || m.concept?.code || m.description || '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{m.reference || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                        {entrada != null ? formatMoney(entrada) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                        {salida != null ? formatMoney(salida) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{formatMoney(saldo)}</td>
                      <td className="px-3 py-2">
                        {link ? (
                          <Link to={link.to} className="text-blue-700 hover:underline text-xs">
                            {link.label}
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(modal === 'in' || modal === 'out') && (
        <ManualMoveModal
          kind={modal}
          concepts={concepts}
          form={manualForm}
          setForm={setManualForm}
          busy={busy}
          onClose={() => {
            setModal(null);
            setError('');
          }}
          onSubmit={() => void submitManual(modal)}
        />
      )}

    </div>
  );
}

function OpenCashModal(props: {
  registers: any[];
  openForm: { cashRegisterId: string; cashAdded: string };
  setOpenForm: (f: { cashRegisterId: string; cashAdded: string }) => void;
  priorBalance: number | null;
  cashAddedNum: number;
  availableAfterOpen: number | null;
  selectedRegister: any;
  busy: boolean;
  error: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const {
    registers,
    openForm,
    setOpenForm,
    priorBalance,
    cashAddedNum,
    availableAfterOpen,
    selectedRegister,
    busy,
    error,
    onClose,
    onConfirm,
  } = props;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold text-slate-900">Abrir caja</h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="block text-sm">
          <span className="font-medium text-slate-700">Caja operativa</span>
          <select
            className="mt-1 w-full border rounded-md px-3 py-2"
            value={openForm.cashRegisterId}
            onChange={(e) => setOpenForm({ ...openForm, cashRegisterId: e.target.value })}
          >
            <option value="">Seleccioná…</option>
            {registers.map((r) => (
              <option key={r.id} value={r.id}>
                {r.branch?.name} — CAJA {r.number}
                {r.isDefault ? ' ★' : ''}
              </option>
            ))}
          </select>
        </label>

        {selectedRegister && (
          <div className="rounded-md bg-slate-50 border px-3 py-2 text-sm space-y-1">
            <p>
              <span className="text-slate-500">Sucursal · </span>
              <strong>{selectedRegister.branch?.name ?? '—'}</strong>
            </p>
            <p>
              <span className="text-slate-500">Saldo previo registrado · </span>
              <strong>
                {priorBalance == null ? '…' : formatMoney(priorBalance)}
              </strong>
            </p>
          </div>
        )}

        <label className="block text-sm">
          <span className="font-medium text-slate-700">Efectivo que agregás al abrir</span>
          <input
            type="number"
            step="0.01"
            min={0}
            className="mt-1 w-full border rounded-md px-3 py-2"
            value={openForm.cashAdded}
            onChange={(e) => setOpenForm({ ...openForm, cashAdded: e.target.value })}
            placeholder="0"
          />
        </label>

        <div className="rounded-md border border-slate-200 px-3 py-2 text-sm space-y-1">
          <p className="text-slate-600">
            Saldo previo {priorBalance == null ? '…' : formatMoney(priorBalance)}
          </p>
          <p className="text-slate-600">+ Efectivo agregado {formatMoney(cashAddedNum)}</p>
          <p className="font-semibold text-slate-900">
            = Disponible después de abrir{' '}
            {availableAfterOpen == null ? '…' : formatMoney(availableAfterOpen)}
          </p>
        </div>

        <p className="text-xs text-slate-500">
          El efectivo informado se suma al saldo actual de la caja. No reemplaza el saldo registrado.
        </p>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-md hover:bg-slate-100">
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || !openForm.cashRegisterId || priorBalance == null}
            onClick={onConfirm}
            className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white disabled:opacity-50"
          >
            {busy ? 'Abriendo…' : 'Confirmar apertura'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ManualMoveModal(props: {
  kind: 'in' | 'out';
  concepts: any[];
  form: { conceptId: string; amount: string; description: string; reference: string };
  setForm: (f: any) => void;
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const { kind, concepts, form, setForm, busy, onClose, onSubmit } = props;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold">
            {kind === 'in' ? 'Registrar ingreso' : 'Registrar egreso'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500">
          Usá movimientos manuales solo cuando el dinero no provenga de una operación automática del
          sistema.
        </p>
        <select
          required
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={form.conceptId}
          onChange={(e) => setForm({ ...form, conceptId: e.target.value })}
        >
          <option value="">Concepto…</option>
          {concepts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} — {c.name}
            </option>
          ))}
        </select>
        <input
          required
          type="number"
          step="0.01"
          min="0.01"
          placeholder="Monto"
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
        />
        <input
          placeholder="Observación"
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <input
          placeholder="Referencia (opcional)"
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={form.reference}
          onChange={(e) => setForm({ ...form, reference: e.target.value })}
        />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-md hover:bg-slate-100">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className={`px-4 py-2 text-sm rounded-md text-white disabled:opacity-50 ${
              kind === 'in' ? 'bg-emerald-700' : 'bg-rose-700'
            }`}
          >
            {busy ? 'Guardando…' : 'Confirmar'}
          </button>
        </div>
      </form>
    </div>
  );
}

